import { useQuery } from '@tanstack/react-query';

import { organizationsApi } from '@/api/organizations.api';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { EnvelopesSection } from '@/pages/dashboard/EnvelopesListPage';

/**
 * Controlled-document admin dashboard. Surfaces org-wide document metrics
 * (totals, by type, review status, non-conforming, unused), the folders
 * overview and the whole organisation's Documents list in one place.
 */
export function AdminDashboardPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin-dashboard'],
    queryFn: () => organizationsApi.adminDashboard(),
  });

  const maxType = Math.max(1, ...(data?.byType.map((b) => b.count) ?? [1]));

  return (
    <AdminLayout title="Admin Dashboard" subtitle="Organisation-wide controlled-document overview.">
      {isLoading || !data ? (
        <div className="card text-center py-12 text-muted text-[13px]">Loading…</div>
      ) : (
        <div className="space-y-6">
          {/* Stat cards */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <StatCard label="Total documents" value={data.total} color="#3b82f6" />
            <StatCard label="Review due (7 days)" value={data.reviewDueSoon} color="#f59e0b" />
            <StatCard label="Review not done" value={data.reviewNotDone} color="#ef4444" />
            <StatCard label="Non-conforming" value={data.nonConformingCount} color="#8b5cf6" />
            <StatCard label="Unused (never opened)" value={data.unused} color="#6b7280" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* By type */}
            <div className="card">
              <h3 className="text-base font-semibold text-gray-900 mb-3">Documents by type</h3>
              {data.byType.length === 0 ? (
                <div className="text-muted text-[13px] py-4 text-center">No data</div>
              ) : (
                <div className="space-y-2.5">
                  {data.byType.map((b) => (
                    <div key={b.type}>
                      <div className="flex justify-between text-[12px] mb-1">
                        <span className="text-gray-700 font-mono">{b.type}</span>
                        <span className="text-muted font-semibold text-gray-900">{b.count}</span>
                      </div>
                      <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-primary"
                          style={{ width: `${(b.count / maxType) * 100}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Folders */}
            <div className="card">
              <h3 className="text-base font-semibold text-gray-900 mb-3">Folders</h3>
              {data.folders.length === 0 ? (
                <div className="text-muted text-[13px] py-4 text-center">No folders</div>
              ) : (
                <ul className="space-y-1.5">
                  {data.folders.map((f) => (
                    <li key={f.id} className="flex items-center gap-2 text-[13px]">
                      <span className="w-3 h-3 rounded-full shrink-0" style={{ background: f.color }} />
                      <span className="text-gray-900 flex-1 truncate">{f.name}</span>
                      <span className="text-muted font-semibold">{f.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Non-conforming details */}
          {data.nonConforming.length > 0 && (
            <div className="card">
              <h3 className="text-base font-semibold text-gray-900 mb-1">Non-conforming documents</h3>
              <p className="text-[12px] text-muted mb-3">
                Same name with different numbers, or same number with different names.
              </p>
              <ul className="space-y-2">
                {data.nonConforming.map((n, i) => (
                  <li key={i} className="border border-border rounded-md px-3 py-2 text-[12px]">
                    <span
                      className={`inline-block text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded mr-2 ${
                        n.kind === 'same_name_diff_number'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-purple-100 text-purple-700'
                      }`}
                    >
                      {n.kind === 'same_name_diff_number' ? 'Same name' : 'Same number'}
                    </span>
                    <code className="text-gray-900">{n.key}</code>
                    <span className="text-muted"> → </span>
                    <span className="text-gray-700">{n.values.join(' · ')}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Whole organisation's Documents */}
          <div>
            <h3 className="text-base font-semibold text-gray-900 mb-3">All organisation documents</h3>
            <EnvelopesSection />
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

function StatCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="card !p-4">
      <div className="text-[11px] uppercase font-semibold text-muted tracking-wider mb-1">{label}</div>
      <div className="text-2xl font-bold" style={{ color }}>
        {value}
      </div>
    </div>
  );
}
